import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EmptyState from '../EmptyState';
import ConfirmDialog from '../ui/ConfirmDialog';
import LoadErrorBox from '../ui/LoadErrorBox';
import StatusBadge from '../ui/StatusBadge';
import { RecordIdLabel } from '../ui/idColumn';
import { useNotifications } from '../NotificationProvider';
import { useFieldErrors, useFormSaveEvent, useClearErrorsOnEdit } from '../forms/_shared';
import AssignmentFields, { AssignmentFieldErrors, AssignmentFormValues } from './AssignmentFields';
import {
  ASSIGNMENT_DELETE_EVENT, ASSIGNMENT_PACK_ALL_EVENT, ASSIGNMENT_SAVE_EVENT, NO_EDITOR, setAssignmentsBar,
} from '../../hooks/useAssignmentsBar';
import ConfirmPackDialog from './ConfirmPackDialog';
import PackingChecklist from './PackingChecklist';
import { useLoad } from '../../hooks/useLoad';
import { usePageRefresh } from '../../hooks/usePageRefresh';
import { useRole } from '../../hooks/useRole';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import {
  bulkPack, createAssignment, deleteAssignment, fetchAssignment, packLine, unpackLine, updateAssignment,
} from '../../services/AsoebiAssignmentService';
import { refreshAssignmentsFromApi, refreshFabricsFromApi } from '../../services/AsoebiService';
import { refreshBundlesFromApi } from '../../services/BundleService';
import { PACKING_LABELS, PACKING_VARIANT, fmtQuantity, packingOf, progressText } from '../../utils/asoebiAssignments';
import { entityPath } from '../../utils/entityRoutes';
import { errorMessageOf, isApiError, validationError } from '../../utils/apiErrors';
import { recordLabel } from '../../utils/recordId';
import type {
  AsoebiAssignmentDetail, AssignmentFabricKind, AssignmentUpdate, ChecklistLine,
} from '../../types/ui/AsoebiAssignment';

const LIST_PATH = '/asoebi/assignments';
const QUANTITY = /^\d{1,12}(\.\d{1,3})?$/;

interface Option { value: string; label: string }
interface PickLists { orders: Option[]; fabrics: Option[]; failed: boolean }

const splitFabric = (value: string): { fabric_id: string | null; fabric_kind: AssignmentFabricKind } =>
  !value ? { fabric_id: null, fabric_kind: 'bundle' }
    : { fabric_id: value.slice(2), fabric_kind: value.startsWith('f:') ? 'product' : 'bundle' };

const toFabricValue = (a: Pick<AsoebiAssignmentDetail, 'fabric_id' | 'fabric_kind'>): string =>
  a.fabric_id ? `${a.fabric_kind === 'product' ? 'f' : 'b'}:${a.fabric_id}` : '';

const blank = (orderId = ''): AssignmentFormValues => ({ orderId, fabricValue: '', quantity: '1', notes: '', status: 'draft' });
const fromDetail = (a: AsoebiAssignmentDetail): AssignmentFormValues => ({
  orderId: a.order_id, fabricValue: toFabricValue(a), quantity: fmtQuantity(a.quantity) || '1', notes: a.notes ?? '', status: a.status,
});

function validate(v: AssignmentFormValues, creating: boolean): AssignmentFieldErrors {
  const errors: AssignmentFieldErrors = {};
  if (creating && !v.orderId) errors.order = 'Choose the order this packs.';
  const q = v.quantity.trim();
  if (!QUANTITY.test(q) || Number(q) <= 0) errors.quantity = 'Enter a number of sets above zero, with at most 3 decimals.';
  return errors;
}

/** Everything the two selects offer. A failed lookup leaves them empty and says so; it never blocks the packing checklist. */
async function loadPickLists(): Promise<PickLists> {
  const [orders, bundles, fabrics] = await Promise.allSettled([
    refreshAssignmentsFromApi(), refreshBundlesFromApi(), refreshFabricsFromApi(),
  ]);
  return {
    failed: [orders, bundles, fabrics].some(r => r.status === 'rejected'),
    orders: orders.status === 'fulfilled'
      ? orders.value.filter(o => !o.read_only).map(o => ({
        value: String(o.id),
        label: [recordLabel('fabricAssignment', o.id), o.guest_name].filter(Boolean).join(' · '),
      }))
      : [],
    fabrics: [
      ...(bundles.status === 'fulfilled' ? bundles.value.map(b => ({ value: `b:${b.id}`, label: `Bundle: ${b.name}` })) : []),
      ...(fabrics.status === 'fulfilled' ? fabrics.value.map(f => ({ value: `f:${f.id}`, label: `Fabric: ${f.name}` })) : []),
    ],
  };
}

interface AssignmentEditorProps {
  /** The id in the URL; undefined when creating. */
  id?: string;
}

/** One assignment as a page (not a popup): its packing checklist, its fields, and Save / Delete / Mark packed in the bar. */
const AssignmentEditor: React.FC<AssignmentEditorProps> = ({ id }) => {
  const creating = id === undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess } = useNotifications();
  const { canEdit } = useRole();

  const detail = useLoad<AsoebiAssignmentDetail | null>(() => (creating ? Promise.resolve(null) : fetchAssignment(id)), [id]);
  const picks = useLoad<PickLists>(loadPickLists, []);
  usePageRefresh(async () => { await Promise.all([detail.reload(), picks.reload()]); }, detail.loading);

  const a = detail.data;
  const presetOrder = useMemo(() => new URLSearchParams(location.search).get('order') ?? '', [location.search]);
  const [values, setValues] = useState<AssignmentFormValues>(() => blank(presetOrder));
  const fieldErrors = useFieldErrors();
  const [saving, setSaving] = useState(false);
  const [busyProduct, setBusyProduct] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [packOpen, setPackOpen] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  // Fill the form from the record once per record id: a refresh must not wipe what is being typed.
  const loadedId = a?.id;
  useEffect(() => { if (a) setValues(fromDetail(a)); }, [loadedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const initial = useMemo(() => (a ? fromDetail(a) : blank(presetOrder)), [a, presetOrder]);
  const dirty = values.orderId !== initial.orderId || values.fabricValue !== initial.fabricValue || values.quantity.trim() !== initial.quantity
    || values.notes !== initial.notes || values.status !== initial.status;
  useUnsavedChangesGuard(dirty && !saving);

  const archivedOrder = a?.archived === true;
  const readOnly = !canEdit || archivedOrder;
  const packedLock = !!a && a.checklist.packed_count > 0;
  const hasChecklist = !!a && a.checklist.total_count > 0;

  const save = useCallback(async () => {
    const found = validate(values, creating);
    if (Object.keys(found).length > 0) throw validationError(found as Record<string, string>);
    const fabric = splitFabric(values.fabricValue);
    setSaving(true);
    try {
      if (creating) {
        const created = await createAssignment({
          order_id: values.orderId, ...fabric, quantity: values.quantity.trim(), notes: values.notes || null,
        });
        showSuccess('Assignment created');
        navigate(entityPath('asoebiAssignment', created.id) ?? LIST_PATH, { replace: true });
        return;
      }
      const patch: AssignmentUpdate = {};
      if (values.fabricValue !== initial.fabricValue) Object.assign(patch, fabric);
      if (values.quantity.trim() !== initial.quantity) patch.quantity = values.quantity.trim();
      if (values.notes !== initial.notes) patch.notes = values.notes || null;
      if (!hasChecklist && values.status !== initial.status) patch.status = values.status;
      const updated = await updateAssignment(id, patch);
      detail.setData(updated);
      setValues(fromDetail(updated));
      showSuccess('Assignment saved');
    } finally {
      setSaving(false);
    }
  }, [values, creating, id, initial, hasChecklist, detail, navigate, showSuccess]);

  // The bar's Save sends the standard form-save event (same as every other form page).
  useFormSaveEvent(save, { fieldErrors, fallback: 'Could not save this assignment.' });
  useClearErrorsOnEdit({ order: values.orderId, orderId: values.orderId, quantity: values.quantity }, fieldErrors, { orderId: 'order_id' });
  const shownErrors: AssignmentFieldErrors = {
    order: fieldErrors.errorFor('order') ?? fieldErrors.errorFor('order_id'),
    quantity: fieldErrors.errorFor('quantity'),
  };

  const toggleLine = async (line: ChecklistLine, packed: boolean) => {
    if (!a) return;
    setBusyProduct(line.product_id);
    setLineErrors(({ [line.product_id]: _gone, ...rest }) => rest);
    try {
      detail.setData(packed ? await packLine(a.id, line.product_id) : await unpackLine(a.id, line.product_id));
    } catch (err) {
      setLineErrors(prev => ({ ...prev, [line.product_id]: errorMessageOf(err, packed ? 'Could not pack this item.' : 'Could not unpack this item.') }));
    } finally {
      setBusyProduct(null);
    }
  };

  const confirmPackAll = async () => {
    if (!a) return;
    setWorking(true);
    setPackError(null);
    try {
      const res = await bulkPack([a.id]);
      const failure = res.results.find(r => !r.ok);
      if (failure) { setPackError(failure.error?.message ?? 'Could not pack this assignment.'); return; }
      setPackOpen(false);
      showSuccess('Assignment marked packed');
      await detail.reload();
    } catch (err) {
      setPackError(errorMessageOf(err, 'Could not mark this packed.'));
    } finally {
      setWorking(false);
    }
  };

  const confirmDelete = async () => {
    if (!a) return;
    setWorking(true);
    setDeleteError(null);
    try {
      await deleteAssignment(a.id);
      setDeleteOpen(false);
      showSuccess('Assignment deleted');
      navigate(LIST_PATH, { replace: true });
    } catch (err) {
      setDeleteError(errorMessageOf(err, 'Could not delete this assignment.'));
    } finally {
      setWorking(false);
    }
  };

  const remaining = a ? a.checklist.total_count - a.checklist.packed_count : 0;
  const canMarkPacked = canEdit && !archivedOrder && !!a && (hasChecklist ? remaining > 0 : a.status !== 'complete');

  // The buttons live in the Header's bar (AssignmentEditorBar): report what they need, take their clicks back.
  const canDelete = !creating && canEdit && !archivedOrder && !!a;
  const canSave = !readOnly;
  useEffect(() => {
    setAssignmentsBar({ editor: { creating, canMarkPacked: !creating && canMarkPacked, canDelete, canSave, saving, dirty } });
  }, [creating, canMarkPacked, canDelete, canSave, saving, dirty]);
  useEffect(() => () => setAssignmentsBar({ editor: NO_EDITOR }), []);
  useEffect(() => {
    const onPackAll = () => { setPackError(null); setPackOpen(true); };
    const onDelete = () => { setDeleteError(null); setDeleteOpen(true); };
    // A failed save throws: useFormSaveEvent shows the field errors under their fields (or a toast), so the bar goes through it.
    const onSave = () => { window.dispatchEvent(new Event('form-save')); };
    window.addEventListener(ASSIGNMENT_PACK_ALL_EVENT, onPackAll);
    window.addEventListener(ASSIGNMENT_DELETE_EVENT, onDelete);
    window.addEventListener(ASSIGNMENT_SAVE_EVENT, onSave);
    return () => {
      window.removeEventListener(ASSIGNMENT_PACK_ALL_EVENT, onPackAll);
      window.removeEventListener(ASSIGNMENT_DELETE_EVENT, onDelete);
      window.removeEventListener(ASSIGNMENT_SAVE_EVENT, onSave);
    };
  }, []);

  if (!creating && detail.loading && !a) {
    return <div className="wt-container"><p className="aa-muted" role="status">Loading assignment…</p></div>;
  }
  if (!creating && detail.error) {
    const status = isApiError(detail.error) ? detail.error.status : null;
    return (
      <>
        <div className="wt-container fade-in">
          {status === 404 || status === 400
            ? <EmptyState icon="package" title="Assignment not found" description="It may have been deleted, or the link is wrong." action={<Link className="ui-btn ui-btn--secondary ui-btn--md" to={LIST_PATH}>Back to assignments</Link>} />
            : status === 403
              ? <EmptyState icon="lock" title="You do not have access to asoebi packing" description="Ask the project owner to give you access." />
              : <LoadErrorBox what="this assignment" error={detail.error} onRetry={detail.reload} retrying={detail.loading} />}
        </div>
      </>
    );
  }

  const packing = a ? packingOf(a) : null;

  const sets = a ? fmtQuantity(a.quantity) : '';

  return (
    <>
      <div className="wt-container fade-in aa-page aa-editor">
        {creating && !canEdit && (
          <EmptyState icon="lock" title="You can view assignments but not create them" />
        )}

        <div className="detail-layout">
          <div className="inline-form-page">
            {archivedOrder && (
              <div className="entity-state-banner entity-state-banner--locked" role="status">
                This assignment belongs to an archived order. Restore the order to change its packing.
              </div>
            )}
            {!creating && !canEdit && !archivedOrder && (
              <div className="entity-state-banner entity-state-banner--partial" role="status">You can view this assignment but not change it.</div>
            )}

            {a && hasChecklist && (
              <section className="form-section" aria-labelledby="aa-checklist-title">
                <h3 id="aa-checklist-title" className="form-section-title">Packing checklist</h3>
                <p className="form-hint">Ticking an item packs the whole quantity and takes it out of stock. Unticking puts it back.</p>
                <PackingChecklist
                  lines={a.checklist.items}
                  readOnly={readOnly}
                  busyProductId={busyProduct}
                  errors={lineErrors}
                  onToggle={toggleLine}
                />
              </section>
            )}

            {a && !hasChecklist && (
              <section className="form-section" aria-labelledby="aa-packing-title">
                <h3 id="aa-packing-title" className="form-section-title">Packing</h3>
                <p className="form-hint">
                  {a.fabric_id
                    ? 'This bundle has no items, or the assignment is for a single fabric, so there is nothing to tick off.'
                    : 'Choose a bundle below to get a packing checklist.'}
                </p>
              </section>
            )}

            {(!creating || canEdit) && (
              <section className="form-section" aria-labelledby="aa-details-title">
                <h3 id="aa-details-title" className="form-section-title">{creating ? 'New assignment' : 'Details'}</h3>
                {picks.data?.failed && (
                  <div className="entity-state-banner entity-state-banner--partial" role="alert">
                    Some orders, bundles or fabrics could not be loaded, so the lists below may be incomplete.{' '}
                    <button type="button" className="aa-linkbutton" onClick={picks.reload}>Retry</button>
                  </div>
                )}
                <AssignmentFields
                  values={values}
                  onChange={setValues}
                  errors={shownErrors}
                  orderOptions={creating ? (picks.data?.orders ?? []) : null}
                  orderLabel={a ? [recordLabel('fabricAssignment', a.order_id), a.guest_name].filter(Boolean).join(' · ') : ''}
                  fabricOptions={picks.data?.fabrics ?? []}
                  readOnly={readOnly}
                  packedLock={packedLock}
                  manualStatus={!creating && !hasChecklist}
                />
              </section>
            )}
          </div>

          <aside className="detail-sidebar">
            <section className="side-section">
              <h4>Assignment Summary</h4>
              {a ? (
                <>
                  <div className="stat-row"><span>Order</span><strong><Link className="aa-link" to={entityPath('fabricAssignment', a.order_id) ?? '/asoebi/orders'}><RecordIdLabel kind="fabricAssignment" id={a.order_id} /></Link></strong></div>
                  <div className="stat-row"><span>Guest</span><strong>{a.guest_name ?? '—'}</strong></div>
                  <div className="stat-row"><span>Bundle / fabric</span><strong>{a.fabric_name ?? '—'}</strong></div>
                  <div className="stat-row"><span>Sets</span><strong>{sets}</strong></div>
                  <div className="stat-row"><span>Packed</span><strong>{progressText(a)}</strong></div>
                  <div className="stat-row"><span>Status</span><strong>{packing && <StatusBadge variant={PACKING_VARIANT[packing]}>{PACKING_LABELS[packing]}</StatusBadge>}</strong></div>
                </>
              ) : (
                <div className="stat-row"><span>Status</span><strong>New</strong></div>
              )}
            </section>
          </aside>
        </div>
      </div>

      <ConfirmPackDialog
        open={packOpen}
        assignments={1}
        lines={hasChecklist ? remaining : 0}
        loading={working}
        error={packError}
        onClose={() => setPackOpen(false)}
        onConfirm={confirmPackAll}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Delete this assignment?"
        subject={a ? `${recordLabel('asoebiAssignment', a.id)}${a.guest_name ? ` for ${a.guest_name}` : ''}` : undefined}
        consequences={['The packing checklist for this order is lost.', 'It can only be deleted while nothing is packed.']}
        confirmLabel="Delete"
        loading={working}
        error={deleteError}
      />
    </>
  );
};

export default AssignmentEditor;
